import React, { useEffect, useState } from 'react';
import { Box, Container, Paper, Typography, TextField } from '@mui/material';
import { TWButton } from './shared/components';
import { buildAuthorizeUrl, setDevLoginToken } from './shared/auth/okta';
import { isAuthEnabled } from './shared/utils/auth';
import { getApiUrl, getApiHeaders, syncAuthSessionAfterLogin } from './shared/utils/api';

const RETURN_TO_KEY = 'onesec_return_to';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Capture ?returnTo= (set by the gateway on unauthenticated browser navigations)
    // or the legacy ?from= param (set by 401 handlers in the SPA) and store it
    // so Callback.tsx can redirect the user back after successful authentication.
    const params = new URLSearchParams(window.location.search);
    const from = params.get('returnTo') ?? params.get('from');
    if (from) {
      try {
        // Only allow same-origin return URLs to prevent open redirect
        const url = new URL(from, window.location.origin);
        if (url.origin === window.location.origin) {
          sessionStorage.setItem(RETURN_TO_KEY, url.pathname + url.search + url.hash);
        }
      } catch {
        // ignore malformed URLs
      }
    }
  }, []);

  const handleOktaLogin = async () => {
    try {
      const url = await buildAuthorizeUrl();
      window.location.href = url;
    } catch (e) {
      console.error(e);
      alert('Okta is not configured. Please set REACT_APP_OKTA_* environment variables.');
    }
  };

  const handleDevLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/auth/dev-login`, {
        method: 'POST',
        credentials: 'include',
        headers: getApiHeaders(),
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || 'Invalid email or password');
        return;
      }
      if (data.access_token && typeof data.expires_in === 'number') {
        setDevLoginToken(data.access_token, data.expires_in);
        await syncAuthSessionAfterLogin(data.access_token);
        const returnTo = sessionStorage.getItem(RETURN_TO_KEY) || '/';
        sessionStorage.removeItem(RETURN_TO_KEY);
        window.location.href = returnTo;
      } else {
        setError('Invalid response from server');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (isAuthEnabled()) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', backgroundColor: '#f5f5f5' }}>
        <Container maxWidth="sm">
          <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <img src="/favicon.ico" alt="OneSec Logo" style={{ width: 28, height: 28, objectFit: 'contain' }} />
              <Typography sx={{ color: '#47A1AD', fontWeight: 700, fontSize: '24px', lineHeight: '31px' }}>
                OneSec
              </Typography>
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Welcome</Typography>
            <Typography variant="body1" sx={{ color: '#555', mb: 3 }}>
              Log in to continue to OneSec Platform.
            </Typography>
            <TWButton onClick={handleOktaLogin} label="Login with Okta" sx={{ width: '100%' }} />
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', backgroundColor: '#f5f5f5' }}>
      <Container maxWidth="sm">
        <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <img src="/favicon.ico" alt="OneSec Logo" style={{ width: 28, height: 28, objectFit: 'contain' }} />
            <Typography sx={{ color: '#47A1AD', fontWeight: 700, fontSize: '24px', lineHeight: '31px' }}>
              OneSec
            </Typography>
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Welcome</Typography>
          <Typography variant="body1" sx={{ color: '#555', mb: 3 }}>
            Log in to continue (local/dev).
          </Typography>
          <form onSubmit={handleDevLogin}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              autoComplete="email"
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="current-password"
            />
            {error && (
              <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                {error}
              </Typography>
            )}
            <TWButton
              type="submit"
              label={loading ? 'Signing in...' : 'Sign in'}
              sx={{ width: '100%', mt: 2 }}
              disabled={loading}
            />
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
